import { PencilSquareIcon } from "@heroicons/react/24/outline";
import React, { forwardRef } from "react";

import { classNames, matchChild } from "../../utils";
import Tooltip from "./Tooltip";

function Panel(props: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={classNames(
        `relative bg-white ring-1 ring-gray-200 dark:ring-gray-50/10 dark:bg-gray-800 shadow rounded-lg overflow-hidden text-sm
        mx-auto text-gray-500 dark:text-gray-200 divide-y dark:divide-black/20 print:shadow-none print:break-inside-avoid`,
        props.className,
      )}
    >
      {props.children}
    </div>
  );
}

Panel.Title = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <h3 className={classNames(className, "text-base block font-semibold leading-6 text-gray-900 dark:text-white")}>
    {children}
  </h3>
);

Panel.Subtitle = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <p
    className={classNames(
      className,
      "mt-1 max-w-2xl text-sm leading-6 text-gray-500 dark:text-gray-400 underlined-links",
    )}
  >
    {children}
  </p>
);

Panel.Header = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <div
    className={classNames(
      className,
      "p-3 sm:p-4 dark:bg-gray-700/30",
      matchChild(children, Panel.Left) && "flex align-center justify-between",
    )}
  >
    {children}
  </div>
);

Panel.ProgressBar = ({ progress }: { progress: number }) => (
  <div className="bg-primary-100 relative h-1 dark:bg-gray-900">
    <div
      className="bg-primary-500 dark:bg-primary-200 absolute -inset-y-px left-0 z-10 transition-all duration-300"
      style={{ width: `${progress * 100}%` }}
    />
  </div>
);

const Content = forwardRef<HTMLDivElement, { children: React.ReactNode; className?: string }>((props, ref) => (
  <div className={classNames(props.className, "p-3 sm:p-4 space-y-4 relative underlined-links normal-lists")} ref={ref}>
    {props.children}
  </div>
));

Panel.Content = Content;

Panel.ContentEmpty = ({ children }: { children: React.ReactNode }) => (
  <div className="underlined-links text-center">{children}</div>
);

Panel.ContentHeading = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <h3
    className={classNames(
      className,
      "text-base font-semibold leading-6 first:mt-0 mt-6 mb-3 text-gray-900 dark:text-white",
    )}
  >
    {children}
  </h3>
);

Panel.Definition = ({
  buttonDescription,
  buttonIcon: ButtonIcon,
  children,
  onButtonClick,
  title,
}: { children: React.ReactNode; title: string } & (
  | { buttonDescription?: string; buttonIcon?: React.ComponentType<{ className?: string }>; onButtonClick: () => void }
  | { buttonDescription?: string; buttonIcon?: undefined; onButtonClick?: undefined }
)) => {
  if (onButtonClick) ButtonIcon ??= PencilSquareIcon;

  return (
    <div
      className={classNames("px-3 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-4", ButtonIcon && "relative pr-10")}
      data-role="definition"
    >
      <dt className="text-sm font-semibold text-gray-500 dark:text-gray-400">{title}</dt>
      <dd className="underlined-links text-sm text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{children}</dd>

      {ButtonIcon && (
        <Tooltip content={buttonDescription}>
          <a className="absolute right-2 top-[17px]" onClick={onButtonClick}>
            <ButtonIcon className="size-4 cursor-pointer text-gray-400 dark:text-gray-500" />
          </a>
        </Tooltip>
      )}
    </div>
  );
};

Panel.SubText = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <span className={classNames("block text-xs leading-5 text-gray-500 dark:text-gray-400", className)}>{children}</span>
);

Panel.Footer = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <div className={classNames("p-3 sm:p-4 bg-slate-100/80 dark:bg-gray-700/20", className)}>{children}</div>
);

Panel.Left = ({ children }: { children: React.ReactNode }) => (
  <div className="flex grow items-center">
    <div>{children}</div>
  </div>
);

Panel.Right = ({ children }: { children: React.ReactNode }) => (
  <div className="flex items-center">
    <div>{children}</div>
  </div>
);

export default Panel;
